import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import {
  deleteProductImage,
  reorderProductImages,
  uploadProductImages,
} from "app/redux/slice/productSlice";
import { CropDialog } from "app/components";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// The app renders product photos at exactly this size, so every upload is
// cropped to it rather than trusting whatever the picker happened to hand us.
const PRODUCT_IMAGE_WIDTH = 960;
const PRODUCT_IMAGE_HEIGHT = 1280;

// The backend stores bare filenames; REACT_APP_IMAGE_BASE_URL already ends in
// "uploads/", so only the module folder needs to be added here.
const buildImageUrl = (filename) =>
  `${process.env.REACT_APP_IMAGE_BASE_URL}product-images/${filename}`;

const ProductImages = ({ productId, images = [], onChanged }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const inputId = `product-image-upload-${productId}`;

  // Picked files waiting to be cropped. Cropping is the only way in: a photo
  // is uploaded the moment its batch finishes, so there is no staging list and
  // no second button to press.
  const [cropQueue, setCropQueue] = useState([]);
  // How many were queued in this batch, so the dialog can say "2 of 3".
  const [cropTotal, setCropTotal] = useState(0);
  // Cropped files collected across the batch, uploaded together once the queue
  // drains. A ref rather than state: it is written from a callback and read by
  // the effect below, and must not schedule a render of its own.
  const croppedRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Local copy so a drag reorders instantly, before the API round-trip.
  const [order, setOrder] = useState(images);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    setOrder(images);
  }, [images]);

  const handleSelect = (event) => {
    const picked = Array.from(event.target.files || []);
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";

    const accepted = [];
    picked.forEach((file) => {
      if (!file.type?.startsWith("image/")) {
        enqueueSnackbar(`${file.name} is not an image file`, {
          variant: "error",
        });
      } else if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`${file.name} is larger than 5MB`, {
          variant: "error",
        });
      } else {
        accepted.push(file);
      }
    });

    // Straight into the cropper - each one is added to the upload list only
    // once it has been cropped to the exact product photo size.
    if (accepted.length) {
      setCropQueue((prev) => [...prev, ...accepted]);
      setCropTotal((prev) => prev + accepted.length);
    }
  };

  const advanceCropQueue = () => setCropQueue((prev) => prev.slice(1));

  const handleCropped = (croppedFile) => {
    croppedRef.current = [...croppedRef.current, croppedFile];
    advanceCropQueue();
  };

  // Cancelling drops just that image; anything still queued is cropped next,
  // and whatever was cropped before it still uploads.
  const handleCropCancel = advanceCropQueue;

  const uploadFiles = async (files) => {
    setUploading(true);

    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));

    const response = await dispatch(
      uploadProductImages({ id: productId, data: formData }),
    ).unwrap();

    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onChanged?.();
    setUploading(false);
  };

  // The batch is finished once the queue drains: upload everything that was
  // cropped, in one request, and reset the counter so the next pick starts at
  // "1 of n".
  useEffect(() => {
    if (cropQueue.length) return;
    setCropTotal(0);
    const files = croppedRef.current;
    if (!files.length) return;
    croppedRef.current = [];
    uploadFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropQueue.length]);

  const handleDelete = async () => {
    const filename = deleteTarget;
    setDeleteTarget(null);

    const response = await dispatch(
      deleteProductImage({ id: productId, filename }),
    ).unwrap();

    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onChanged?.();
  };

  const persistOrder = async (nextOrder) => {
    setSavingOrder(true);
    const response = await dispatch(
      reorderProductImages({ id: productId, images: nextOrder }),
    ).unwrap();

    const isSuccess = response.status === 200;
    if (!isSuccess) {
      enqueueSnackbar(response.data?.responseMessage || "Could not save order", {
        variant: "error",
      });
      setOrder(images); // roll back to what the server still has
    } else {
      onChanged?.();
    }
    setSavingOrder(false);
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    const next = [...order];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);

    setDragIndex(null);
    setOrder(next);
    persistOrder(next);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="h5" mb={0}>
          Photos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {order.length
            ? `${order.length} uploaded — drag to reorder, first photo is the main one`
            : "None uploaded yet"}
        </Typography>
        {savingOrder && <CircularProgress size={16} />}
      </Stack>

      <input
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleSelect}
      />
      <label htmlFor={inputId}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
            mb: 2,
            border: "2px dashed #ccc",
            borderRadius: 2,
            minHeight: 110,
            cursor: "pointer",
            "&:hover": { borderColor: "primary.main" },
          }}
        >
          <CloudUploadIcon color="primary" fontSize="large" />
          <Typography variant="body1" mb={0}>
            Click to upload photos
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={0}>
            JPG, PNG or WEBP — up to 5MB each, multiple allowed
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={0}>
            Every photo is cropped to {PRODUCT_IMAGE_WIDTH} ×{" "}
            {PRODUCT_IMAGE_HEIGHT} px before upload
          </Typography>
        </Box>
      </label>

      {uploading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary" mb={0}>
            Uploading…
          </Typography>
        </Stack>
      )}

      <CropDialog
        open={cropQueue.length > 0}
        file={cropQueue[0]}
        index={cropTotal - cropQueue.length}
        total={cropTotal}
        width={PRODUCT_IMAGE_WIDTH}
        height={PRODUCT_IMAGE_HEIGHT}
        label="Product Photo"
        onCropped={handleCropped}
        onCancel={handleCropCancel}
      />

      {order.length > 0 && (
        <Grid container spacing={2}>
          {order.map((filename, index) => (
            <Grid item xs={6} sm={4} md={3} key={filename}>
              <Paper
                elevation={3}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(index)}
                onDragEnd={() => setDragIndex(null)}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "grab",
                  opacity: dragIndex === index ? 0.4 : 1,
                  border: index === 0 ? 2 : 0,
                  borderColor: "primary.main",
                }}
              >
                {/* Position badge - index 0 is what the app shows as the
                    product thumbnail. */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    bgcolor:
                      index === 0 ? "primary.main" : "rgba(0,0,0,0.55)",
                    color: "common.white",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 13 }} />
                  {index === 0 ? "1 · Main" : index + 1}
                </Box>

                <Tooltip title="Delete photo">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(filename)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(255,255,255,0.85)",
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>

                {/* Photos are stored at 960x1280, so the card is shown at that
                    same 3:4 shape and the whole picture fits inside it.
                    `contain` rather than `cover` because photos uploaded before
                    the cropper existed are other shapes, and cutting into them
                    to fill the card is what made the view look wrong. */}
                <img
                  src={buildImageUrl(filename)}
                  alt={filename}
                  style={{
                    width: "100%",
                    aspectRatio: `${PRODUCT_IMAGE_WIDTH} / ${PRODUCT_IMAGE_HEIGHT}`,
                    objectFit: "contain",
                    backgroundColor: "#f5f5f5",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <DialogConfirm
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Are you sure you want to delete this photo?"
        onConfirm={handleDelete}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
      />
    </Box>
  );
};

export default ProductImages;
